import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Badge,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import { get, post_new, del } from "../../helpers/api_helper";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const initialState = {
  id: null,
  category_id: "",
  name: "",
  credits: "",
  price: "",
  currency: "RON",
  is_active: true,
};

const CreditPackages = () => {
  document.title = "Pachete Credite | PagaSunt Admin";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [packages, setPackages] = useState([]);
  const [meta, setMeta] = useState({});
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    category_id: "",
    page: 1,
  });
  const [currentPackage, setCurrentPackage] = useState(initialState);

  const fetchCategories = async () => {
    try {
      const response = await get("/admin/categories");
      if (response.status === "success") {
        setCategories((response.data.flat || []).filter(cat => cat.parent_id === null));
      }
    } catch (error) {
      console.error("Eroare la încărcarea categoriilor:", error);
    }
  };

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const query = `category_id=${filters.category_id}&page=${filters.page}`;
      const response = await get(`/admin/credit-packages?${query}`);
      if (response.status === "success") {
        setPackages(response.data.data || []);
        setMeta(response.data || {});
      }
    } catch (error) {
      console.error("Eroare la încărcarea pachetelor de credite:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [filters.page, filters.category_id]);

  const toggleModal = () => {
    setModal(!modal);
    if (modal) {
      setIsEdit(false);
      setCurrentPackage(initialState);
    }
  };

  const handleFilterChange = e => {
    setFilters({ ...filters, [e.target.name]: e.target.value, page: 1 });
  };

  const handlePageChange = page => {
    setFilters({ ...filters, page });
  };

  const handleEdit = pkg => {
    setCurrentPackage({
      id: pkg.id,
      category_id: pkg.category_id,
      name: pkg.name || "",
      credits: pkg.credits || "",
      price: pkg.price_ron ?? ((pkg.price || 0) / 100).toFixed(2),
      currency: pkg.currency || "RON",
      is_active: !!pkg.is_active,
    });
    setIsEdit(true);
    setModal(true);
  };

  const handleDelete = async id => {
    if (!window.confirm("Sigur vrei să ștergi acest pachet de credite?")) {
      return;
    }

    try {
      await del(`/admin/credit-packages/${id}`);
      fetchPackages();
    } catch (error) {
      console.error("Eroare la ștergerea pachetului:", error);
      alert("A apărut o eroare la ștergere.");
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      category_id: currentPackage.category_id,
      name: currentPackage.name,
      credits: Number(currentPackage.credits),
      price: Number(currentPackage.price),
      currency: currentPackage.currency,
      is_active: currentPackage.is_active,
    };

    try {
      if (isEdit) {
        await post_new(`/admin/credit-packages/${currentPackage.id}`, payload);
      } else {
        await post_new("/admin/credit-packages", payload);
      }

      toggleModal();
      fetchPackages();
    } catch (error) {
      console.error("Eroare la salvarea pachetului:", error);
      alert(error?.response?.data?.message || "A apărut o eroare la salvare.");
    } finally {
      setSaving(false);
    }
  };

  const selectedCategoryName = id =>
    categories.find(cat => String(cat.id) === String(id))?.name || "-";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="PagaSunt" breadcrumbItem="Pachete Credite" />

          <Row className="mb-3">
            <Col className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">Pachete pentru categoriile principale</h5>
                <small className="text-muted">Un pachet defineste cate anunturi primeste utilizatorul intr-o categorie principala.</small>
              </div>
              <Button color="primary" onClick={toggleModal}>
                <i className="mdi mdi-plus me-1"></i> Adaugă Pachet
              </Button>
            </Col>
          </Row>

          <Card>
            <CardBody>
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label>Categorie principală</Label>
                    <Input
                      type="select"
                      name="category_id"
                      value={filters.category_id}
                      onChange={handleFilterChange}
                    >
                      <option value="">Toate categoriile</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              {loading ? (
                <div className="text-center p-5">
                  <Spinner color="primary" />
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="table-centered table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Categorie</th>
                        <th>Pachet</th>
                        <th>Credite</th>
                        <th>Preț</th>
                        <th>Status</th>
                        <th>Actualizat</th>
                        <th>Acțiuni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {packages.length > 0 ? (
                        packages.map(pkg => (
                          <tr key={pkg.id}>
                            <td>{pkg.category?.name || selectedCategoryName(pkg.category_id)}</td>
                            <td>
                              <strong>{pkg.name}</strong>
                            </td>
                            <td>{pkg.credits}</td>
                            <td>{pkg.price_ron ?? ((pkg.price || 0) / 100).toFixed(2)} {pkg.currency}</td>
                            <td>
                              <Badge color={pkg.is_active ? "success" : "secondary"}>
                                {pkg.is_active ? "Activ" : "Inactiv"}
                              </Badge>
                            </td>
                            <td>{new Date(pkg.updated_at).toLocaleDateString("ro-RO")}</td>
                            <td>
                              <Button size="sm" color="link" onClick={() => handleEdit(pkg)}>
                                Edit
                              </Button>
                              <Button size="sm" color="link" className="text-danger" onClick={() => handleDelete(pkg.id)}>
                                Șterge
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center text-muted py-4">
                            Nu există pachete pentru filtrul selectat.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>

                  {meta.last_page > 1 && (
                    <div className="mt-4 d-flex justify-content-center">
                      <Pagination>
                        <PaginationItem disabled={meta.current_page === 1}>
                          <PaginationLink previous onClick={() => handlePageChange(meta.current_page - 1)} />
                        </PaginationItem>
                        {[...Array(meta.last_page)].map((_, i) => (
                          <PaginationItem active={i + 1 === meta.current_page} key={i}>
                            <PaginationLink onClick={() => handlePageChange(i + 1)}>
                              {i + 1}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationItem disabled={meta.current_page === meta.last_page}>
                          <PaginationLink next onClick={() => handlePageChange(meta.current_page + 1)} />
                        </PaginationItem>
                      </Pagination>
                    </div>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      <Modal isOpen={modal} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          {isEdit ? "Editează pachetul" : "Pachet nou"}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Categorie principală</Label>
              <Input
                type="select"
                value={currentPackage.category_id}
                onChange={e => setCurrentPackage({ ...currentPackage, category_id: e.target.value })}
                required
              >
                <option value="">Selectează categoria</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Input>
            </FormGroup>

            <FormGroup>
              <Label>Nume pachet</Label>
              <Input
                type="text"
                value={currentPackage.name}
                onChange={e => setCurrentPackage({ ...currentPackage, name: e.target.value })}
                placeholder="Ex: Pachet 10 anunțuri"
                required
              />
            </FormGroup>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Număr credite</Label>
                  <Input
                    type="number"
                    min="1"
                    value={currentPackage.credits}
                    onChange={e => setCurrentPackage({ ...currentPackage, credits: e.target.value })}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Preț (RON)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={currentPackage.price}
                    onChange={e => setCurrentPackage({ ...currentPackage, price: e.target.value })}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Monedă</Label>
                  <Input
                    type="select"
                    value={currentPackage.currency}
                    onChange={e => setCurrentPackage({ ...currentPackage, currency: e.target.value })}
                  >
                    <option value="RON">RON</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Status</Label>
                  <Input
                    type="select"
                    value={currentPackage.is_active ? "1" : "0"}
                    onChange={e => setCurrentPackage({ ...currentPackage, is_active: e.target.value === "1" })}
                  >
                    <option value="1">Activ</option>
                    <option value="0">Inactiv</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Button color="primary" type="submit" className="w-100" disabled={saving}>
              {saving ? "Se salvează..." : (isEdit ? "Actualizează pachetul" : "Salvează pachetul")}
            </Button>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default CreditPackages;
